import { ContactsApiService } from './crm/ContactsApiService.js';
import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { mapContactFromApi, mapContactToApi } from './crm/ApiEntityMapper.js';
import { generateId, nowIso } from '../utils/IdUtils.js';
import { AccountContactService } from './AccountContactService.js';
import { auth } from './AuthService.js';
import { AuditService } from './AuditService.js';
import { formatDateDisplay } from '../utils/Utils.js';
import { UserService } from './UserService.js';
import { ClientService } from './ClientService.js';
import { CacheService } from './CacheService.js';
import { EMAIL_ERROR_MESSAGE, isValidEmail } from '../utils/EmailValidation.js';
import { SyncManager } from '../reference/SyncManager.js';

export class ContactService {
    static async getAll() {
        return CrmApiRepository.getAllContacts();
    }

    static async getLookup() {
        return CrmApiRepository.getAllContactsLookup();
    }

    static async getById(id) {
        const row = await ContactsApiService.getById(id);
        return row ? mapContactFromApi(row) : null;
    }

    static async getByClient(clientId, primaryContactId = null) {
        return AccountContactService.getContactsForAccount(clientId, primaryContactId);
    }

    static async findByEmail(email) {
        const normalized = (email || '').trim().toLowerCase();
        if (!normalized) return null;
        const contacts = await ContactService.getAll();
        return contacts.find(c => (c.email || '').trim().toLowerCase() === normalized) || null;
    }

    static async getEnrichedAll() {
        const cacheKey = 'contacts:enriched:all';
        return CacheService.getOrSet(cacheKey, async () => {
            await SyncManager.ensureCoreReference().catch(() => {});
            const [contacts, clients, dealLinks, users] = await Promise.all([
                ContactService.getAll(),
                ClientService.getAll(),
                CrmApiRepository.getDealContactLinks().catch(() => []),
                UserService.getLookupUsers().catch(() => []),
            ]);
            const clientMap = Object.fromEntries(clients.map(c => [c.id, c.name]));
            const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));
            const dealsByContact = {};
            const ownersByContact = {};
            for (const deal of dealLinks) {
                const cid = deal.primaryContactId;
                if (!cid) continue;
                if (!dealsByContact[cid]) dealsByContact[cid] = new Set();
                dealsByContact[cid].add(deal.id);
                if (deal.ownerUserId) {
                    if (!ownersByContact[cid]) ownersByContact[cid] = new Set();
                    ownersByContact[cid].add(deal.ownerUserId);
                }
            }
            return contacts.map(c => {
                const clientId = c.clientId;
                const accountNames = clientId && clientMap[clientId] ? [clientMap[clientId]] : [];
                const dealCount = dealsByContact[c.id]?.size || 0;
                const ownerIds = ownersByContact[c.id] ? [...ownersByContact[c.id]] : [];
                const ownerLabel = ownerIds.length === 1
                    ? (userMap[ownerIds[0]] || '—')
                    : (ownerIds.length > 1 ? 'Multiple' : '—');
                return {
                    ...c,
                    accountNames,
                    accountNamesLabel: accountNames.length ? accountNames.join(', ') : '—',
                    fullName: ContactService.fullName(c),
                    roleLabel: c.title || '—',
                    dealCount,
                    ownerLabel,
                    createdByName: userMap[c.createdBy] || '—',
                    updatedByName: userMap[c.updatedBy] || '—',
                    createdAtDisplay: formatDateDisplay(c.createdAt),
                    updatedAtDisplay: formatDateDisplay(c.updatedAt)
                };
            });
        }, 2 * 60 * 1000);
    }

    static async save(contact, clientId) {
        // Prevent duplicate contact rows: when creating a new contact whose email
        // already exists, reuse the existing contact (update it) instead of inserting
        // a duplicate. Best-effort — if the lookup fails, fall back to the create path.
        if (!contact.id && contact.email) {
            try {
                const existing = await ContactService.findByEmail(contact.email);
                if (existing) contact.id = existing.id;
            } catch (err) {
                console.warn('Contact email dedup lookup failed; proceeding with create:', err);
            }
        }
        const ts = nowIso();
        let prev = null;
        if (contact.id) {
            try {
                prev = await ContactService.getById(contact.id);
            } catch (err) {
                // If edit is opened with a stale/non-existent id, allow save as create
                // instead of failing the quick-create flow.
                if (err?.statusCode !== 404) throw err;
            }
        }
        const session = auth.getSession();
        const shouldUpdate = !!(contact.id && prev);
        const record = {
            id: shouldUpdate ? contact.id : generateId('contact'),
            firstName: contact.firstName?.trim(),
            lastName: contact.lastName?.trim() || '',
            email: contact.email?.trim() || '',
            phone: contact.phone || '',
            title: contact.title || '',
            linkedIn: contact.linkedIn || '',
            notes: contact.notes || '',
            status: contact.status || prev?.status || 'active',
            clientId: clientId !== undefined
                ? clientId
                : (contact.clientId !== undefined ? contact.clientId : (prev?.clientId ?? null)),
            createdBy: prev?.createdBy || contact.createdBy || session?.userId || null,
            updatedBy: session?.userId || contact.updatedBy || null,
            createdAt: prev?.createdAt || contact.createdAt || ts,
            updatedAt: ts
        };

        if (!record.firstName) throw new Error('First name is required.');
        if (!record.email) throw new Error('Email is required.');
        if (!isValidEmail(record.email)) throw new Error(EMAIL_ERROR_MESSAGE);

        const apiPayload = mapContactToApi(record, record.clientId);
        let saved;
        if (shouldUpdate) {
            window._suppressRealtimeToastFor?.('contact.updated', contact.id);
            window._suppressRealtimeRefreshFor?.('contact.updated', contact.id);
            saved = await ContactsApiService.update(contact.id, apiPayload);
        } else {
            saved = await ContactsApiService.create(apiPayload);
        }
        Object.assign(record, mapContactFromApi(saved));
        if (record.id) {
            const wsType = shouldUpdate ? 'contact.updated' : 'contact.created';
            window._suppressRealtimeToastFor?.(wsType, record.id);
            window._suppressRealtimeRefreshFor?.(wsType, record.id);
        }

        if (prev) {
            await AuditService.logFieldChanges({
                entityType: 'contact',
                entityId: record.id,
                action: 'contact.fields_changed',
                before: prev,
                after: record,
                fields: ['firstName', 'lastName', 'email', 'phone', 'title', 'linkedIn', 'status']
            });
        } else {
            await AuditService.log({
                entityType: 'contact',
                entityId: record.id,
                action: 'contact.created',
                after: { name: ContactService.fullName(record), email: record.email }
            });
        }
        window.dispatchEvent(new CustomEvent('contactSaved', { detail: { contact: record } }));
        return record;
    }

    static async delete(id) {
        const prev = await ContactService.getById(id);
        if (!prev) return;
        window._suppressRealtimeToastFor?.('contact.deleted', id);
        window._suppressRealtimeRefreshFor?.('contact.deleted', id);
        await ContactsApiService.softDelete(id);
        CrmApiRepository.clearCache();
        await AuditService.log({
            entityType: 'contact',
            entityId: id,
            action: 'contact.disabled',
            before: { status: prev.status || 'active' },
            after: { status: 'inactive' }
        });
        window.dispatchEvent(new CustomEvent('contactDeleted', { detail: { id } }));
    }

    static fullName(c) {
        return `${c.firstName || ''} ${c.lastName || ''}`.trim();
    }
}
